import { InfoCircleOutlined, SaveFilled } from "@ant-design/icons"
import { Button, Flex, Form, Modal, Typography } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import React, { useState } from "react"

import Icon from "../../../../Icon"
import ReactRouterPrompt from "../../../../components/ReactRouterPrompt.tsx"
import { MAIN_CONTENT_STICKY_BOTTOM_CLASS } from "../../../../components/main-content.ts"
import { type TeamMembersFilterOptions } from "../../../TeamManagementPage/redux/team-members.types.ts"
import { SettingsCard } from "../common/SettingsCard.tsx"
import { SettingsCompanyFormChangedWatcher } from "./SettingsCompanyFormChangedWatcher.tsx"
import { CompanyInformationFormSection } from "./sections/CompanyInformationFormSection.tsx"
import { CompanyLicensingFormSection } from "./sections/CompanyLicensingFormSection.tsx"
import { CompanyOperationalDefaultsFormSection } from "./sections/CompanyOperationalDefaultsFormSection.tsx"

export interface SettingsCompanyFormData {
  business_name: string
  company_phone: string
  license_number: string
  bond_number?: string
  ein?: string
  street_address?: string
  city?: string
  state?: string
  zip_code?: string
  general_liability?: string
  logo?: string | File | null
  insurance_number?: string
  lead_cost_method?: string
  company_website?: string
  company_description?: string
  receiving_account: string
  company_email?: string
  default_tax_pct?: string | number | null
  default_retainage_pct?: string | number | null
  cash_flow_threshold_percent?: string | number | null
  contract_tolerance_percent?: string | number | null
}

interface SettingsCompanyFormProps {
  form: FormInstance<SettingsCompanyFormData>
  initialValues?: SettingsCompanyFormData
  options: TeamMembersFilterOptions | null
  readonly?: boolean
  saving: boolean
  // Scopes card titles and field labels to the active workspace — "Branch
  // Name"/"Branch Logo"/"Branch Bio" inside a branch, "Company Name"/
  // "Company Logo"/"Company Bio" in Company HQ. Licensing fields are
  // labelled the same either way: `CompanyDetails.license_number` etc.
  // are genuinely per-branch columns with no shared legal-entity record
  // behind them (confirmed by reading the model, not assumed), so there
  // is no "shared" legal identity here to preserve a distinct label for.
  isBranchScope?: boolean
  // The active branch's own name, for the sticky footer's scope notice —
  // only meaningful (and only passed) when `isBranchScope` is true.
  branchName?: string
  onFinish: (data: SettingsCompanyFormData) => void
  onReset: () => void
}

export function SettingsCompanyForm({
  form,
  options,
  readonly,
  initialValues,
  saving,
  isBranchScope,
  branchName,
  onReset,
  onFinish
}: SettingsCompanyFormProps) {
  const [isDirty, setIsDirty] = useState(false)

  return (
    <>
      <ReactRouterPrompt when={isDirty}>
        {({ isActive, onConfirm, onCancel }) => (
          <Modal
            open={isActive}
            title="Are you sure you want to leave?"
            onOk={onConfirm}
            onCancel={onCancel}
            okText="Leave"
            cancelText="Stay"
          >
            <p>You have unsaved changes. If you leave, they will be lost.</p>
          </Modal>
        )}
      </ReactRouterPrompt>

      <Form
        form={form}
        clearOnDestroy
        onFinish={onFinish}
        layout="vertical"
        className={"flex-1 flex gap-3 flex-col w-full with-default-error"}
        validateTrigger={["onSubmit", "onChange"]}
        disabled={saving || readonly}
        initialValues={initialValues}
      >
        <SettingsCompanyFormChangedWatcher
          form={form}
          initialValues={initialValues}
          setIsDirty={setIsDirty}
          saving={saving}
        />

        {/* Single column, original grouping: who we are (identity, contact,
            address, logo, bio), what we're licensed as, what we default to —
            stacked, not split into a main-column/sidebar arrangement. */}
        <div
          className="flex flex-col gap-3 flex-1"
          data-testid="company-settings-sections"
        >
          <SettingsCard
            iconWrapperClassName="bg-blue-100"
            icon={<Icon type={"Office"} className="text-blue-600" />}
            title={isBranchScope ? "Branch Information" : "Company Information"}
          >
            <CompanyInformationFormSection
              form={form}
              initialValues={initialValues}
              options={options}
              isBranchScope={isBranchScope}
            />
          </SettingsCard>
          <SettingsCard
            iconWrapperClassName="bg-sky-100"
            icon={<Icon type={"Shield"} className="text-sky-600" />}
            title="Licensing & insurance"
          >
            <CompanyLicensingFormSection form={form} />
          </SettingsCard>
          <SettingsCard
            iconWrapperClassName="bg-amber-100"
            icon={<Icon type={"Settings"} className="text-amber-600" />}
            title="Operational defaults"
          >
            <CompanyOperationalDefaultsFormSection options={options} />
          </SettingsCard>
        </div>

        <Flex
          vertical
          gap={"small"}
          justify={"space-between"}
          className={`lg:flex-row max-lg:items-center ${MAIN_CONTENT_STICKY_BOTTOM_CLASS} z-[2] bg-background py-2`}
        >
          <Flex align={"center"} gap={"small"}>
            <InfoCircleOutlined className="text-gray-400" />
            <Typography.Text type="secondary" className="text-xs">
              {isBranchScope
                ? `Changes apply to ${branchName ?? "this branch"} only.`
                : "Company profile changes do not overwrite branch overrides."}
            </Typography.Text>
          </Flex>

          <Flex
            gap={"small"}
            className={"max-lg:w-full max-lg:justify-between "}
          >
            <Button disabled={!isDirty || saving} onClick={onReset}>
              Cancel Changes
            </Button>
            <Button
              htmlType="submit"
              disabled={!isDirty}
              loading={saving}
              icon={<SaveFilled />}
              type={"primary"}
            >
              Save Changes
            </Button>
          </Flex>
        </Flex>
      </Form>
    </>
  )
}
